// ============================================================
// Dubai Sell listings, read for the pre-login showcase.
//
// Dubai Sell is a separate Lovable + Supabase platform; Asset Desk's
// backend already proxies its `properties` table on
// GET /api/sell-platform/listings (Backend/sellPlatformRoutes.js), which
// needs no auth and returns only rows with status = 'listed'. So the
// showcase reads real inventory rather than carrying its own copy.
//
// The base URL is derived exactly the way App.tsx and PropertyView do,
// and the /api suffix is stripped before re-adding it — pasting them
// together is what produced the api/api/ bug in earlier builds.
// ============================================================

const API_ORIGIN = (
  process.env.REACT_APP_API_URL || 'https://investmenter-backend-1081814658115.europe-west1.run.app'
).replace(/\/api\/?$/, '');

export interface SellListing {
  id: string;
  title: string | null;
  propertyType: string | null;
  location: string | null;
  sizeSqft: number | null;
  bedrooms: number | null;
  askingPrice: number | null;
  marketValue: number | null;
  isDistress: boolean;
  discountPercent: number | null;
  description: string | null;
  photoUrl: string | null;
  // Sent by backends from 2026-10-23 on; older ones omit them.
  urgency?: string | null;
  viewType?: string | null;
  unitPosition?: string | null;
  handoverDate?: string | null;
  highlightTags?: string[];
}

export interface SellListingsResult {
  listings: SellListing[];
  total: number;
}

/** Fetches listed units. Rejects on network/HTTP failure so callers can show an error state. */
export async function fetchSellListings(limit = 50): Promise<SellListingsResult> {
  const res = await fetch(`${API_ORIGIN}/api/sell-platform/listings?limit=${limit}`);
  if (!res.ok) throw new Error(`Listings request failed (${res.status})`);

  const data = await res.json();
  if (!data?.success) throw new Error(data?.error || 'Listings unavailable');

  // Only cards that can actually carry the design are shown — a unit with no
  // photo would render as an empty frame in an image-led gallery.
  const listings: SellListing[] = (data.listings || []).filter((l: SellListing) => l.photoUrl);

  return { listings, total: Number(data.total) || listings.length };
}

/**
 * Every listed unit, photo or not — for the in-app IM Properties list, where
 * a unit without a photo still belongs (it gets a plain placeholder frame).
 */
export async function fetchAllSellListings(): Promise<SellListingsResult> {
  const res = await fetch(`${API_ORIGIN}/api/sell-platform/listings?limit=500`);
  if (!res.ok) throw new Error(`Listings request failed (${res.status})`);
  const data = await res.json();
  if (!data?.success) throw new Error(data?.error || 'Listings unavailable');
  const listings: SellListing[] = data.listings || [];
  return { listings, total: Number(data.total) || listings.length };
}

/** AED, compacted for cards: 4415299 -> "AED 4.42 M". */
export function formatAed(value: number | null): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (value >= 1_000_000) return `AED ${(value / 1_000_000).toFixed(2)} M`;
  if (value >= 1_000) return `AED ${Math.round(value / 1_000)} K`;
  return `AED ${value}`;
}

/** Full AED figure with thousands separators, for the detail sheet. */
export function formatAedFull(value: number | null): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return `AED ${value.toLocaleString('en-AE', { maximumFractionDigits: 0 })}`;
}

export interface EnquiryInput {
  listing: SellListing;
  name: string;
  email: string;
  phone?: string;
  message?: string;
}

/**
 * Posts a buyer enquiry. The backend saves it to the Dubai Sell `leads`
 * table and pings the team on Slack (POST /enquire in
 * Backend/sellPlatformRoutes.js), so this is a real lead, not a mailto.
 *
 * Field names and the extra listing detail match what MainDashboard already
 * sends, so both surfaces land in the same shape.
 */
export async function submitEnquiry({ listing, name, email, phone, message }: EnquiryInput): Promise<void> {
  const res = await fetch(`${API_ORIGIN}/api/sell-platform/enquire`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      listingId: listing.id,
      propertyType: listing.propertyType,
      location: listing.location,
      askingPrice: listing.askingPrice,
      bedrooms: listing.bedrooms,
      sizeSqft: listing.sizeSqft,
      buyerName: name,
      buyerEmail: email,
      buyerPhone: phone || '',
      message: message || '',
    }),
  });

  if (!res.ok) throw new Error(`Enquiry failed (${res.status})`);
  const data = await res.json().catch(() => ({}));
  if (data && data.success === false) throw new Error(data.error || 'Enquiry failed');
}

/**
 * Developers represented in the current inventory, most units first.
 *
 * Derived by matching a known-developer list against each listing's title and
 * description, because Dubai Sell's `properties` rows carry no developer
 * column. That keeps the "available from" band honest: it can only ever name
 * developers we actually have units from right now.
 */
const KNOWN_DEVELOPERS = [
  'Emaar', 'Meraas', 'Nakheel', 'Omniyat', 'Select Group', 'Sobha',
  'Beyond', 'Imtiaz', 'Samana', 'Avenew', 'DAMAC', 'Binghatti',
  'Danube', 'Ellington', 'Azizi', 'Arada',
];

export function developersInInventory(listings: SellListing[]): { name: string; count: number }[] {
  const counts = new Map<string, number>();

  listings.forEach((l) => {
    const haystack = `${l.title || ''} ${l.description || ''}`;
    KNOWN_DEVELOPERS.forEach((dev) => {
      // Word-boundary match: a bare substring would let "Arada" hit
      // "Barada" and similar.
      if (new RegExp(`\\b${dev}\\b`, 'i').test(haystack)) {
        counts.set(dev, (counts.get(dev) || 0) + 1);
      }
    });
  });

  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export interface WaitlistInput {
  name: string;
  email: string;
  phone?: string;
  note?: string;
  answers?: Record<string, string>;
  locale?: string;
}

/**
 * Fractional ownership waiting list. Posts to the backend, which does the
 * Close CRM work — creating the lead if it is new, updating only the agreed
 * custom fields if it is not, and logging a Touchpoint activity. The Close
 * API key stays server-side.
 */
export async function joinFractionalWaitlist(input: WaitlistInput): Promise<{ dryRun: boolean }> {
  const res = await fetch(`${API_ORIGIN}/api/fractional/waitlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.error || `Waiting list request failed (${res.status})`);
  }
  return { dryRun: !!data?.dryRun };
}
